import { GridColumn as Column } from '@progress/kendo-react-grid';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DropdownCell } from 'src/components/shared/DataGrid/EditableGrid/DropdownCell';
import { EditableGrid } from 'src/components/shared/DataGrid/EditableGrid/EditableGrid';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { GradeCorrectionDto, GradeDetailDto } from 'src/store/grades/grade';
import { selectActiveInstruments } from 'src/store/instruments/instruments.selectors';
import { selectActiveParameters } from 'src/store/parameters/parameters.selectors';
import { useAuth } from '../../Auth/AuthContext';

interface Props {
  grade: Nullable<GradeDetailDto>;
  onCorrectionsChanged: (corrections: GradeCorrectionDto[]) => void;
}

let uniqueId = 0;

export const GradeCorrections: React.FC<Props> = ({
  grade,
  onCorrectionsChanged
}) => {
  const { t } = useTranslation();

  const [corrections, setCorrections] = useState<GradeCorrectionDto[]>([]);
  const [editedCorrections, setEditedCorrections] = useState<
    GradeCorrectionDto[]
  >([]);

  const [error, setError] = useState<Nullable<string>>(null);

  const { user } = useAuth();
  const parameters$ = useSelector(selectActiveParameters);
  const instruments$ = useSelector(selectActiveInstruments);

  useEffect(() => {
    if (grade) {
      setCorrections(grade.gradeCorrections);
      setEditedCorrections(grade.gradeCorrections);
    }
  }, [grade]);

  const checkParameterInstrumentCombination = (
    correction: GradeCorrectionDto
  ) => {
    const exists = corrections.some(
      gc =>
        gc.id !== correction.id &&
        gc.parameterId === correction.parameterId &&
        gc.instrumentId === correction.instrumentId
    );
    setError(exists ? t('grade.msgCombinationExists') : null);
  };

  const onCancel = () => {
    const newCorrections = [...(grade?.gradeCorrections ?? [])];
    setCorrections(newCorrections);
    setEditedCorrections(newCorrections);
  };

  const onAddCorrection = () => {
    const correction: GradeCorrectionDto = {
      id: --uniqueId,
      gradeId: grade?.id,
      userId: user?.id
    };
    setCorrections([correction, ...editedCorrections]);
    setEditedCorrections([correction, ...editedCorrections]);
  };

  const onDeleteCorrection = (correction: GradeCorrectionDto) => {
    const newCorrections = editedCorrections.filter(
      c => c.id !== correction.id
    );
    setCorrections(newCorrections);
    setEditedCorrections(newCorrections);
  };

  const CorrectionsList = useMemo(() => {
    return (
      <EditableGrid
        items={corrections}
        onItemsChange={items => {
          setEditedCorrections(items);
          onCorrectionsChanged(items);
        }}
        style={{ height: 350 }}
      >
        <Column
          field="parameterId"
          title={t('parameter.singular')}
          width="220px"
          cell={props => (
            <DropdownCell
              {...props}
              items={parameters$}
              uniqueField="id"
              optionField="name"
              onItemChange={() => {
                checkParameterInstrumentCombination(props.dataItem);
              }}
            />
          )}
        />

        <Column
          field="instrumentId"
          title={t('instrument.singular')}
          width="220px"
          cell={props => (
            <DropdownCell
              {...props}
              items={instruments$}
              uniqueField="id"
              optionField="name"
              onItemChange={() => {
                checkParameterInstrumentCombination(props.dataItem);
              }}
            />
          )}
        />

        <Column
          field="correctionPart"
          title={t('grade.correctionPart')}
          editor="numeric"
        />
        <Column
          width="60px"
          filterable={false}
          editable={false}
          cell={props => (
            <td>
              <DeleteButton
                onClick={() => onDeleteCorrection(props.dataItem)}
              />
            </td>
          )}
        />
      </EditableGrid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [corrections, instruments$, parameters$]);

  return (
    <Fragment>
      <div className="flex align-center mb-24">
        <SecondaryButton
          icon="la-plus"
          onClick={onAddCorrection}
          className="mr-24"
        >
          {t('add.verb')}
        </SecondaryButton>
        <TertiaryButton onClick={onCancel} className="mr-24">
          {t('undo')}
        </TertiaryButton>
        {error && (
          <BodyType style={{ color: 'var(--red-status-color)' }}>
            {error}
          </BodyType>
        )}
      </div>
      {CorrectionsList}
    </Fragment>
  );
};
